/**
 * Entity Name Batcher — display-name-only sibling of {@link entityFetchBatcher}.
 *
 * Some surfaces (e.g. association highlight cards resolving an owner/company
 * name) need *only* an entity's display name, not its full property set or
 * associations. This batcher fetches just the display-name properties via the
 * `properties` subset param on `by-entity-ids`, then writes the result with
 * `upsertSummaryObjects` — which merges into the store WITHOUT marking the
 * entry `isComplete`. That distinction matters: a name-only fetch must not
 * flip an entity to "complete" and thereby starve a richer card (contact /
 * company / deal cards) that still needs the full object — `hasCompleteObject`
 * stays false, so those callers' own full fetch still fires.
 *
 * Like the full batcher: collects ids over a 50ms window, dedupes in-flight
 * requests, chunks to the API limit, and exposes a failure signal so callers
 * can exit their loading state instead of spinning forever.
 */

import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import {
  beginBatch,
  endBatch,
  getObject,
  hasCompleteObject,
  registerEntityAlias,
  resolveEntityId,
  upsertSummaryObjects,
} from '@/lib/crm/store';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { logger } from '@/lib/logger';

/**
 * Properties needed to resolve a display name across entity types:
 * `getDisplayName` reads `ahoy_display_name` first, then falls back to
 * name / first+last. Requesting all of them keeps the resolver entity-type
 * agnostic while staying far smaller than a full hydrate.
 */
export const NAME_PROPERTIES: readonly string[] = [
  'ahoy_display_name',
  'ahoy_name',
  'ahoy_first_name',
  'ahoy_last_name',
];

/** Batch window in milliseconds (matches entityFetchBatcher). */
const BATCH_WINDOW_MS = 50;

/** Maximum IDs per API request (backend memory limit on property prefetch). */
const MAX_CHUNK_SIZE = 100;

const pendingByOrg = new Map<number, Set<string>>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();
const inflight = new Set<string>();

const failedEntityIds = new Set<string>();
const failureListeners = new Set<() => void>();
let failureVersion = 0;

function notifyFailureListeners(): void {
  failureVersion += 1;
  for (const listener of failureListeners) {
    listener();
  }
}

/**
 * True when the store already has a non-empty display name for this entity.
 * Resolves through the merge-alias map first so a pre-merge (requested) id
 * whose name was fetched under its canonical post-merge id is recognised as
 * resolved — otherwise a merged id would be re-fetched indefinitely.
 */
function hasResolvableName(entityId: string): boolean {
  const obj = getObject(resolveEntityId(entityId));
  return obj ? getDisplayName(obj, '') !== '' : false;
}

/**
 * Ensure an entity's display name is in the store. No-op when the name is
 * already resolvable, the full object is already complete, or a fetch is
 * already in flight. Synchronous and non-blocking — call from hooks/effects.
 */
export function ensureEntityName(orgId: number, entityId: string): void {
  if (hasResolvableName(entityId) || hasCompleteObject(entityId)) {
    return;
  }
  if (inflight.has(entityId)) {
    return;
  }

  // Re-queueing a previously-failed entity counts as a retry.
  if (failedEntityIds.delete(entityId)) {
    notifyFailureListeners();
  }

  let pending = pendingByOrg.get(orgId);
  if (!pending) {
    pending = new Set();
    pendingByOrg.set(orgId, pending);
  }
  pending.add(entityId);

  if (!timers.has(orgId)) {
    timers.set(
      orgId,
      setTimeout(() => flushOrg(orgId), BATCH_WINDOW_MS)
    );
  }
}

/** Subscribe to name-fetch failure notifications. Returns an unsubscribe fn. */
export function subscribeToNameFetchFailures(listener: () => void): () => void {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
}

/** Check whether the most recent name fetch for an entity failed. */
export function hasNameFetchFailed(entityId: string): boolean {
  return failedEntityIds.has(entityId);
}

/** Snapshot value for `useSyncExternalStore` — bumped on every failure change. */
export function getNameFailureVersion(): number {
  return failureVersion;
}

async function flushOrg(orgId: number): Promise<void> {
  timers.delete(orgId);
  const pending = pendingByOrg.get(orgId);
  pendingByOrg.delete(orgId);

  if (!pending || pending.size === 0) {
    return;
  }

  const toFetch: string[] = [];
  for (const id of pending) {
    if (!hasResolvableName(id) && !hasCompleteObject(id) && !inflight.has(id)) {
      toFetch.push(id);
      inflight.add(id);
    }
  }

  if (toFetch.length === 0) {
    return;
  }

  try {
    const client = createCRMObjectClient(orgId, 'ahoy_object');

    const chunks: string[][] = [];
    for (let i = 0; i < toFetch.length; i += MAX_CHUNK_SIZE) {
      chunks.push(toFetch.slice(i, i + MAX_CHUNK_SIZE));
    }

    const results = await Promise.allSettled(
      chunks.map(chunk =>
        client.getByEntityIds(chunk, {
          includeAssociations: false,
          properties: [...NAME_PROPERTIES],
        })
      )
    );

    beginBatch();
    let failureDelta = false;
    try {
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (result.status === 'fulfilled') {
          const fetched = new Set<string>(Object.keys(result.value));
          // Summary upsert: merges name props without marking the entry
          // complete, so richer cards still trigger their own full fetch.
          upsertSummaryObjects(Object.values(result.value));
          // Register merge aliases so a pre-merge (requested) id resolves to
          // the canonical post-merge entry. getByEntityIds keys its response
          // by the REQUESTED id, while upsertSummaryObjects stores under
          // obj.entity_id — without the alias a requested pre-merge id would
          // never find the fetched name and would spin forever. Mirrors
          // entityFetchBatcher's full-object path.
          for (const [requestedId, obj] of Object.entries(result.value)) {
            if (requestedId !== obj.entity_id) {
              registerEntityAlias(requestedId, obj.entity_id);
            }
          }
          for (const id of chunks[i]) {
            if (!fetched.has(id) && !failedEntityIds.has(id)) {
              failedEntityIds.add(id);
              failureDelta = true;
            }
          }
        } else {
          logger.error(
            'Entity name batch chunk failed',
            result.reason instanceof Error
              ? result.reason
              : new Error(String(result.reason))
          );
          for (const id of chunks[i]) {
            if (!failedEntityIds.has(id)) {
              failedEntityIds.add(id);
              failureDelta = true;
            }
          }
        }
      }
    } finally {
      endBatch();
    }
    if (failureDelta) {
      notifyFailureListeners();
    }
  } catch (error) {
    logger.error(
      'Entity name batch fetch failed',
      error instanceof Error ? error : new Error(String(error))
    );
    let failureDelta = false;
    for (const id of toFetch) {
      if (!failedEntityIds.has(id)) {
        failedEntityIds.add(id);
        failureDelta = true;
      }
    }
    if (failureDelta) {
      notifyFailureListeners();
    }
  } finally {
    for (const id of toFetch) {
      inflight.delete(id);
    }
  }
}
